<template>
  <div class="summary-audio" :class="{ 'is-playing': playbackState.playing, 'is-loading': playbackState.loading, 'is-compact': compact, 'has-duration': playbackState.duration > 0 }">
    <div class="summary-audio-row">
      <button ref="playButton" type="button" class="summary-audio-toggle" :disabled="!audioRequest || translationPending" :aria-label="buttonLabel" @pointerup="releasePointerFocus" @pointercancel="releasePointerFocus" @click="handlePlayClick">
        <span class="summary-audio-icon" aria-hidden="true">
          <span v-if="playbackState.loading" class="summary-audio-spinner"></span>
          <svg v-else-if="playbackState.playing" viewBox="0 0 20 20"><path d="M6 5v10M14 5v10" fill="none" stroke="currentColor" stroke-width="3" /></svg>
          <svg v-else viewBox="0 0 20 20"><path d="M7 4.5 16 10l-9 5.5Z" fill="currentColor" /></svg>
        </span>
        <span class="summary-audio-copy">
          <span class="summary-audio-label">{{ playbackLabel }}</span>
          <span class="summary-audio-byline">Henry’s AI voice</span>
        </span>
      </button>
      <span class="summary-audio-time" :class="{ 'is-pending': playbackState.duration <= 0 }">
        <template v-if="playbackState.duration > 0">{{ clock(playbackState.currentTime) }} / {{ clock(playbackState.duration) }}</template>
        <template v-else>Summary audio</template>
      </span>
    </div>
    <div class="summary-audio-track">
      <input v-if="playbackState.duration > 0" class="summary-audio-seek" type="range" min="0" :max="playbackState.duration" step="0.1" :value="playbackState.currentTime" :style="{ '--audio-progress': `${progressPercent}%` }" aria-label="Summary playback position" :aria-valuetext="`${clock(playbackState.currentTime)} of ${clock(playbackState.duration)}`" @input="seek">
      <span v-else class="summary-audio-rail" aria-hidden="true"><span :class="{ 'is-active': playbackState.loading }"></span></span>
    </div>
    <p v-if="playbackState.error" class="summary-audio-error" role="status">{{ playbackState.error }}</p>
    <p v-else-if="!audioRequest" class="summary-audio-error">Audio will be available once this View is saved.</p>
    <span class="summary-audio-status" role="status">{{ playbackState.loading ? loadingLabel : '' }}</span>
    <p v-if="notice" class="summary-audio-error" role="status">{{ notice }}</p>
    <audio v-if="!playback" ref="audio" preload="none" @loadedmetadata="updateTime" @durationchange="updateTime" @timeupdate="updateTime" @play="playing = true" @playing="loading = false" @waiting="loading = playing" @pause="handleSummaryPause" @ended="handleSummaryEnd" @error="playbackError" />
    <ViewAudioVersions v-if="adminOpen" :share-id="shareId" @close="closeAdmin" @audio-selected="selectAudio" />
  </div>
</template>

<script>
import { setSpeechPlaybackRate } from '../../utils/speechPlayback';
import { askEndpoints } from '../../utils/askSite';
import { summaryAudioRequest } from '../../utils/viewAudio';
import ViewAudioVersions from './ViewAudioVersions.vue';

export default {
  name: 'ViewSummaryAudio',
  components: { ViewAudioVersions },
  props: { playback: { type: Object, default: null }, compact: Boolean, translationPending: Boolean, language: { type: String, default: '' }, sourceLanguage: { type: String, default: '' }, shareId: { type: String, default: '' }, audioSrc: { type: String, default: '' } },
  emits: ['audio-updated', 'progress', 'toggle', 'seek', 'pause'],
  data: () => ({ audioUrl: '', loading: false, playing: false, currentTime: 0, duration: 0, error: '',
    refreshedSrc: '', notice: '', adminOpen: false }),
  computed: {
    playbackState() { return this.playback || this; },
    audioRequest() { return summaryAudioRequest(askEndpoints()[0], this.shareId, this.refreshedSrc || this.audioSrc, { language: this.language, sourceLanguage: this.sourceLanguage, streamEndpoint: process.env.VUE_APP_EXHIBITION_AUDIO_ENDPOINT }); },
    loadingLabel() { return this.audioRequest?.method === 'GET' ? 'Loading your audio…' : 'Preparing your audio…'; },
    playbackLabel() {
      const { loading, playing, currentTime, complete } = this.playbackState;
      if (loading) return this.loadingLabel;
      if (playing) return 'Playing summary';
      if (complete) return 'Replay summary';
      if (currentTime > 0) return 'Continue summary';
      return 'Listen to summary';
    },
    progressPercent() { const { duration, currentTime } = this.playbackState; return duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0; },
    buttonLabel() {
      if (this.playbackState.loading) return 'Cancel loading summary audio';
      return `${this.playbackState.playing ? 'Pause' : 'Play'} summary in Henry’s AI voice`;
    },
  },
  mounted() {
    this._onOtherAudio = (event) => {
      if (event.detail === this) return;
      if (this.loading) this.reset();
      else this.$refs.audio?.pause();
    };
    window.addEventListener('view-summary-play', this._onOtherAudio);
  },
  beforeUnmount() {
    window.removeEventListener('view-summary-play', this._onOtherAudio);
    this.reset();
  },
  watch: { audioRequest() { this.reset(); }, translationPending() { this.reset(); }, shareId() {
    this.refreshedSrc = ''; this.notice = ''; this.adminOpen = false; this._playClicks = [];
    this.reset();
  } },
  methods: {
    handlePlayClick(event) {
      // Ignore key-repeat. Five deliberate clicks/taps form the hidden action.
      if (event?.detail === 0 && event?.repeat) return;
      const now = performance.now();
      const clicks = this._playClicks || [];
      this._playClicks = clicks.length && now - clicks[clicks.length - 1] <= 500
        ? [...clicks, now].filter(time => now - time <= 2000) : [now];
      if (this.shareId && this._playClicks.length >= 5) {
        this._playClicks = [];
        this.reset();
        if (this.playback) this.$emit('pause');
        this.notice = '';
        this.adminOpen = true;
        return;
      }
      // Only the first click in a rapid sequence controls playback.
      if (this._playClicks.length === 1) this.toggle();
    },
    closeAdmin() {
      this.adminOpen = false; this._playClicks = [];
      this.$nextTick(() => this.$refs.playButton?.focus({ preventScroll: true }));
    },
    selectAudio(audio) {
      this.reset();
      this.refreshedSrc = audio.src;
      this.$emit('audio-updated', audio);
    },
    reset() {
      this._playGeneration = (this._playGeneration || 0) + 1;
      this._request?.abort();
      this._request = null;
      this.$refs.audio?.pause();
      this.$refs.audio?.removeAttribute('src');
      this.$refs.audio?.load();
      this.audioUrl = '';
      this.loading = false;
      this.playing = false;
      this.currentTime = 0;
      this.duration = 0;
      this.error = '';
    },
    async toggle() {
      if (this.playback) { this.$emit('toggle'); return; }
      if (this.loading) { this.reset(); return; }
      if (this.playing) { this.$refs.audio.pause(); return; }
      if (!this.audioRequest || this.translationPending) return;
      const generation = this._playGeneration || 0;
      this.error = '';
      this.notice = '';
      if (!this.audioUrl && this.audioRequest.method === 'GET') this.audioUrl = this.audioRequest.url;
      if (!this.audioUrl) {
        const controller = new AbortController();
        this._request = controller;
        this.loading = true;
        const timeout = setTimeout(() => controller.abort(), 70000);
        try {
          const { url, method } = this.audioRequest;
          let response;
          do {
            response = await fetch(url, { method, signal: controller.signal });
            if (response.status !== 202) break;
            await response.body?.cancel();
            const seconds = Math.min(5, Math.max(1, Number(response.headers.get('Retry-After')) || 2));
            await new Promise((resolve, reject) => {
              const timer = setTimeout(resolve, seconds * 1000);
              controller.signal.addEventListener('abort', () => {
                clearTimeout(timer);
                reject(controller.signal.reason);
              }, { once: true });
            });
          } while (!controller.signal.aborted);
          if (!response.ok || !response.headers.get('content-type')?.startsWith('audio/')) throw new Error('unavailable');
          // A successful preparation response means the recording is saved.
          // Native GET playback can now stream that asset without resynthesising it.
          await response.body?.cancel();
          if (this._request !== controller) return;
          this.audioUrl = url;
        } catch {
          if (this._request === controller) this.error = 'Audio is temporarily unavailable. Tap play to try again.';
          return;
        } finally {
          clearTimeout(timeout);
          if (this._request === controller) { this._request = null; this.loading = false; }
        }
      }
      if (generation !== (this._playGeneration || 0)) return;
      const audio = this.$refs.audio;
      if (!audio || !this.audioUrl) return;
      if (audio.getAttribute('src') !== this.audioUrl) audio.src = this.audioUrl;
      if (audio.ended) audio.currentTime = 0;
      window.dispatchEvent(new CustomEvent('view-summary-play', { detail: this }));
      this.loading = true;
      setSpeechPlaybackRate(audio);
      try { await audio.play(); } catch {
        if (generation !== (this._playGeneration || 0)) return;
        window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this }));
        this.error = 'Your audio is ready. Tap play to listen.';
      } finally { if (generation === (this._playGeneration || 0)) this.loading = false; }
    },
    handleSummaryPause() {
      this.playing = false;
      window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this }));
    },
    handleSummaryEnd() {
      this.playing = false;
      window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this }));
    },
    updateTime() {
      const audio = this.$refs.audio;
      this.duration = Number.isFinite(audio?.duration) ? audio.duration : 0;
      this.currentTime = audio?.currentTime || 0;
      this.$emit('progress', { currentTime: this.currentTime, duration: audio?.duration, playing: this.playing });
    },
    seek(event) {
      const time = Number(event.target.value);
      if (this.playback) { this.$emit('seek', time); return; }
      this.$refs.audio.currentTime = time; this.updateTime();
    },
    releasePointerFocus(event) { event.currentTarget?.blur(); },
    clock(seconds) { const n = Math.floor(seconds || 0); return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`; },
    playbackError() {
      if (this.audioUrl) { this.reset(); this.error = 'Audio could not be played. Tap play to try again.'; }
    },
  },
};
</script>

<style scoped>
.summary-audio {
  --audio-track: color-mix(in srgb, var(--view-ink, #333) 11%, transparent);
  --audio-fill: color-mix(in srgb, var(--view-ink, #333) 56%, transparent);
  width: 100%;
  margin: 1.15rem 0 1.75rem;
  color: var(--view-ink, #333);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif;
}
.summary-audio-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; min-width: 0; }
.summary-audio-toggle {
  display: inline-flex;
  align-items: center;
  gap: .72rem;
  min-width: 0;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.summary-audio-toggle:disabled { opacity: .42; cursor: default; }
.summary-audio-icon {
  display: grid;
  place-items: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 50%;
  background: color-mix(in srgb, currentColor 5.5%, transparent);
  transition: transform 110ms ease-out, background-color 150ms ease, box-shadow 150ms ease;
}
.summary-audio-icon svg { width: 16px; height: 16px; }
.summary-audio-toggle:hover .summary-audio-icon,
.summary-audio-toggle:focus-visible .summary-audio-icon,
.is-playing .summary-audio-icon,
.is-loading .summary-audio-icon { background: color-mix(in srgb, currentColor 9%, transparent); }
.summary-audio-toggle:active .summary-audio-icon {
  transform: scale(.94);
  background: color-mix(in srgb, currentColor 12%, transparent);
}
.summary-audio-toggle:focus-visible { outline: none; }
.summary-audio-toggle:focus-visible .summary-audio-icon { box-shadow: 0 0 0 3px color-mix(in srgb, var(--view-accent, #99713b) 24%, transparent); }
.summary-audio-copy { display: flex; min-width: 0; flex-direction: column; gap: .12rem; }
.summary-audio-label { font-size: .84rem; font-weight: 590; line-height: 1.25; letter-spacing: .002em; }
.summary-audio-byline { font-size: .7rem; color: color-mix(in srgb, var(--view-muted, #777) 84%, transparent); line-height: 1.3; }
.summary-audio-time {
  flex: 0 0 auto;
  color: color-mix(in srgb, var(--view-muted, #777) 82%, transparent);
  font-size: .68rem;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  white-space: nowrap;
}
.summary-audio-time.is-pending { opacity: .7; }
.summary-audio-track { height: 18px; margin: .05rem 0 0 3.12rem; }
.summary-audio-seek {
  --audio-progress: 0%;
  display: block;
  width: 100%;
  height: 18px;
  margin: 0;
  padding: 0;
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  outline: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  touch-action: none;
}
.summary-audio-seek::-webkit-slider-runnable-track {
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(to right, var(--audio-fill) var(--audio-progress), var(--audio-track) var(--audio-progress));
}
.summary-audio-seek::-moz-range-track {
  height: 2px;
  border: 0;
  border-radius: 999px;
  background: var(--audio-track);
}
.summary-audio-seek::-moz-range-progress {
  height: 2px;
  border-radius: 999px;
  background: var(--audio-fill);
}
.summary-audio-seek::-webkit-slider-thumb {
  width: 10px;
  height: 10px;
  margin-top: -4px;
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 1px 3px color-mix(in srgb, currentColor 18%, transparent);
  opacity: 0;
  transform: scale(.72);
  transition: opacity 120ms ease, transform 120ms ease;
}
.summary-audio-seek::-moz-range-thumb {
  width: 10px;
  height: 10px;
  border: 0;
  border-radius: 50%;
  background: currentColor;
  opacity: 0;
  transform: scale(.72);
  transition: opacity 120ms ease, transform 120ms ease;
}
.summary-audio-seek:hover::-webkit-slider-thumb,
.summary-audio-seek:focus-visible::-webkit-slider-thumb,
.summary-audio-seek:active::-webkit-slider-thumb { opacity: 1; transform: scale(1); }
.summary-audio-seek:hover::-moz-range-thumb,
.summary-audio-seek:focus-visible::-moz-range-thumb,
.summary-audio-seek:active::-moz-range-thumb { opacity: 1; transform: scale(1); }
.summary-audio-seek:focus-visible { outline: none; }
.summary-audio-rail {
  display: block;
  position: relative;
  top: 8px;
  width: 100%;
  height: 2px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--audio-track);
}
.summary-audio-rail span {
  display: block;
  width: 24%;
  height: 100%;
  border-radius: inherit;
  background: color-mix(in srgb, currentColor 30%, transparent);
  opacity: 0;
}
.summary-audio-rail span.is-active { opacity: 1; animation: audio-prepare 1.5s ease-in-out infinite alternate; }
.summary-audio-error { max-width: 30rem; margin: .35rem 0 0 3.12rem; font-size: .76rem; line-height: 1.4; color: var(--view-muted, #777); }
.summary-audio-spinner { width: 13px; height: 13px; border: 1.5px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: audio-spin 900ms linear infinite; }
.summary-audio-status { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
:global(html.dark-mode .summary-audio),
:global(body.dark-mode .summary-audio) {
  --audio-track: rgba(215, 215, 215, .18);
  --audio-fill: rgba(215, 215, 215, .72);
}
:global(html.dark-mode .summary-audio-byline),
:global(body.dark-mode .summary-audio-byline),
:global(html.dark-mode .summary-audio-time),
:global(body.dark-mode .summary-audio-time) {
  color: var(--site-text-tertiary);
}
:global(html.dark-mode .summary-audio-time.is-pending),
:global(body.dark-mode .summary-audio-time.is-pending) {
  opacity: 0.9;
}
.summary-audio.is-compact { margin: 0; }
.is-compact .summary-audio-toggle { min-height: 36px; gap: 10px; }
.is-compact .summary-audio-icon { width: 32px; height: 32px; flex-basis: 32px; }
.is-compact .summary-audio-icon svg { width: 13px; height: 13px; }
.is-compact .summary-audio-label { font-family: 'Quattrocento Sans', sans-serif; font-size: 13px; font-weight: 400; }
.is-compact .summary-audio-byline { font-family: 'Quattrocento Sans', sans-serif; font-size: 10px; }
.is-compact .summary-audio-time { font-size: 10px; }
.is-compact .summary-audio-time.is-pending { visibility: hidden; }
.is-compact .summary-audio-track { margin-left: 42px; }
.is-compact:not(.has-duration):not(.is-loading) .summary-audio-track { visibility: hidden; }
@keyframes audio-spin { to { transform: rotate(360deg); } }
@keyframes audio-prepare { from { transform: translateX(-100%); } to { transform: translateX(410%); } }
@media (max-width: 520px) {
  .summary-audio { width: 100%; }
  .summary-audio-track { margin-left: 3rem; }
  .summary-audio-time { font-size: .64rem; }
}
@media (prefers-reduced-motion: reduce) {
  .summary-audio-icon,
  .summary-audio-seek::-webkit-slider-thumb,
  .summary-audio-seek::-moz-range-thumb { transition: none; }
  .summary-audio-spinner,
  .summary-audio-rail span.is-active { animation: none; }
  .summary-audio-rail span.is-active { opacity: .5; }
}
</style>
